import { useMemo, useState } from "react";
import { toast } from "react-toastify";
import { MdCheck, MdContentCopy, MdOpenInNew } from "react-icons/md";
import { User } from "../../../features/users/usersTypes";
import {
  buildPublicProfileSlug,
  getPublicProfileUrl,
  getReferralUrlForUser,
} from "../../../utils/publicProfile";

type ProfileShareLinksSectionProps = {
  user?: User;
};

type ShareLinkBlockProps = {
  label: string;
  url: string;
  emptyText: string;
};

function ShareLinkBlock({ label, url, emptyText }: ShareLinkBlockProps) {
  const [copied, setCopied] = useState(false);

  const handleCopy = async () => {
    if (!url) return;
    try {
      await navigator.clipboard.writeText(url);
      setCopied(true);
      window.setTimeout(() => setCopied(false), 2000);
      toast.success(`${label} copied`);
    } catch {
      toast.error("Could not copy link");
    }
  };

  const handleOpen = () => {
    if (!url) return;
    window.open(url, "_blank", "noopener,noreferrer");
  };

  return (
    <div className="profile-share-links__item">
      <span className="profile-share-links__label">{label}</span>
      <div className="profile-share-links__card">
        <p className={`profile-share-links__url${url ? "" : " is-empty"}`}>
          {url || emptyText}
        </p>
        <div className="profile-share-links__actions">
          <button
            type="button"
            className={`profile-share-links__btn profile-share-links__btn--copy${
              copied ? " is-copied" : ""
            }`}
            onClick={() => void handleCopy()}
            disabled={!url}
          >
            {copied ? (
              <>
                <MdCheck aria-hidden />
                Copied
              </>
            ) : (
              <>
                <MdContentCopy aria-hidden />
                Copy link
              </>
            )}
          </button>
          <button
            type="button"
            className="profile-share-links__btn profile-share-links__btn--open"
            onClick={handleOpen}
            disabled={!url}
            title={`Open ${label.toLowerCase()}`}
          >
            <MdOpenInNew aria-hidden />
            Open
          </button>
        </div>
      </div>
    </div>
  );
}

function EmbedCodeBlock({ code }: { code: string }) {
  const [copied, setCopied] = useState(false);

  const handleCopy = async () => {
    if (!code) return;
    try {
      await navigator.clipboard.writeText(code);
      setCopied(true);
      window.setTimeout(() => setCopied(false), 2000);
      toast.success("Embed code copied");
    } catch {
      toast.error("Could not copy embed code");
    }
  };

  return (
    <div className="profile-share-links__item">
      <span className="profile-share-links__label">Embed on your website</span>
      <p className="profile-share-links__hint">
        Paste this HTML where you want your customer reviews to appear.
      </p>
      <div className="profile-share-links__card">
        <pre className="profile-share-links__embed-code">
          <code>{code}</code>
        </pre>
        <div className="profile-share-links__actions">
          <button
            type="button"
            className={`profile-share-links__btn profile-share-links__btn--copy${
              copied ? " is-copied" : ""
            }`}
            onClick={() => void handleCopy()}
          >
            {copied ? (
              <>
                <MdCheck aria-hidden />
                Copied
              </>
            ) : (
              <>
                <MdContentCopy aria-hidden />
                Copy code
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
}

const ProfileShareLinksSection = ({ user }: ProfileShareLinksSectionProps) => {
  const publicProfileUrl = useMemo(() => {
    if (!user?.user_id) return "";
    return getPublicProfileUrl(buildPublicProfileSlug(user));
  }, [user]);

  const referralUrl = useMemo(
    () => getReferralUrlForUser(user),
    [user],
  );

  const embedCode = useMemo(() => {
    if (!publicProfileUrl) return "";
    return `<iframe src="${publicProfileUrl}" title="Customer reviews" loading="lazy" style="width:100%;max-width:920px;height:640px;border:0;border-radius:16px;"></iframe>`;
  }, [publicProfileUrl]);

  return (
    <div className="profile-identity__panel">
      <h3 className="profile-identity__panel-title">Links</h3>
      <div className="profile-share-links profile-share-links--sidebar">
        <ShareLinkBlock
          label="Public profile"
          url={publicProfileUrl}
          emptyText="Profile link unavailable"
        />
        <ShareLinkBlock
          label="Referral"
          url={referralUrl}
          emptyText="Referral link unavailable"
        />
        {embedCode && <EmbedCodeBlock code={embedCode} />}
      </div>
    </div>
  );
};

export default ProfileShareLinksSection;
